import AddCategoryModal from "@/src/components/action-modals/add-category-modal";
import CategoriesTable from "@/src/components/dashboard/category-table";
import { type IconProps } from "@/src/components/members/stats-card";
import StatsRow from "@/src/components/members/stats-row";
import UserDashboardHeader from "@/src/components/members/user-dashboard-header";
import ScButton from "@/src/components/ui-kit/button";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import SectionHeader from "@/src/components/ui-kit/section-header";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import { useChatbot } from "@/src/hooks/features/chatbot/use-chatbot";
import { CategoryTag } from "@/src/models/category.model";
import { useInsertCategoryMutation } from "@/src/services/api/category/category.hooks";
import { useGetAgentWidgetId } from "@/src/services/api/chatbot/chatbot.hooks";
import { useGetEventsQuery } from "@/src/services/api/events/events.hooks";
import { useGetGroups } from "@/src/services/api/groups/groups.hooks";
import { useAllNews } from "@/src/services/api/news/news.hooks";
import { useGetPostsQuery } from "@/src/services/api/posts/posts.hooks";
import { useGetOrganizationConfigQuery } from "@/src/services/organization/organization.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import {
  ArchiveBook,
  Calendar,
  Filter,
  People,
  TableDocument,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Pressable, ScrollView, Text, View } from "react-native";

type StatItem = Readonly<{
  count: number;
  text: string;
  icon: React.ReactElement<IconProps>;
}>;

const Dashboard = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [filterTrigger, setFilterTrigger] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filteredTotalCount, setFilteredTotalCount] = useState(0);

  const { mutate: insertCategory, isPending: isInserting } =
    useInsertCategoryMutation();

  // Content counts
  const { data: postsData } = useGetPostsQuery();
  const { data: newsData } = useAllNews();
  const { data: eventsData } = useGetEventsQuery({
    filter: JSON.stringify({}),
    sort: JSON.stringify({}),
    pageSize: 1,
  });
  const { data: groupsData } = useGetGroups();

  const totalPosts = postsData?.pages?.[0]?.getposts?.totalCount ?? 0;
  const totalNews = newsData?.pages?.[0]?.totalCount ?? 0;
  const totalEvents = eventsData?.pages?.[0]?.totalCount ?? 0;
  const totalGroups = groupsData?.pages?.[0]?.getgroups?.totalCount ?? 0;

  const iconColor = themeVars["--color-chalk-default"] as string;

  const stats: ReadonlyArray<StatItem> = [
    {
      count: totalPosts,
      text: "LABEL_TOTAL_POSTS",
      icon: <TableDocument color={iconColor} />,
    },
    {
      count: totalNews,
      text: "LABEL_TOTAL_NEWS",
      icon: <ArchiveBook color={iconColor} />,
    },
    {
      count: totalEvents,
      text: "LABEL_TOTAL_EVENTS",
      icon: <Calendar color={iconColor} />,
    },
    {
      count: totalGroups,
      text: "LABEL_TOTAL_GROUPS",
      icon: <People color={iconColor} />,
    },
  ];

  const tagCountLabel = `${activeFilterCount > 0 ? filteredTotalCount : 0} ${t("LABEL_TAGS")}`;

  const handleAddCategory = (categoryName: string, tag: CategoryTag | null) => {
    insertCategory(
      { categoryName, tags: tag ? [tag] : [] },
      { onSuccess: () => setIsAddModalVisible(false) },
    );
  };

  const {
    isSettingUp,
    handleSetupChatbot,
    chatbotConfig,
    isDisabling,
    handleDisableChatbot,
  } = useChatbot();

  const isChatbotEnabled = !!chatbotConfig?.widgetId;
  const { data: agentData } = useGetAgentWidgetId(chatbotConfig?.itemId ?? "");
  const agent = agentData?.agents?.[0];

  const { data: orgConfig } = useGetOrganizationConfigQuery();

  // Determine if chatbot can be enabled
  const hasOrgConfig = !!orgConfig;
  const canEnableChatbot = hasOrgConfig && !isChatbotEnabled;

  return (
    <View className="flex-row flex-1 bg-obsidian">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View className="flex-1 flex-col rounded-2xl gap-2">
          <UserDashboardHeader bgImageName="office" />
          <StatsRow stats={stats} />

          {/* Categories card */}
          <View className="bg-chalk rounded-2xl pt-6 px-4 xl:px-6 pb-2 gap-4 md:border md:border-ash-3">
            <View className="border-b-2 border-primary self-start px-1 pb-2">
              <Text className="text-primary font-semibold text-sm">
                {t("LABEL_CATEGORIES")}
              </Text>
            </View>

            <View className="flex-row items-stretch gap-3 xl:justify-between">
              <ScSearchInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderKey="PLACEHOLDER_SEARCH_CATEGORIES"
                containerClassName="rounded-2xl flex-1 xl:max-w-xl"
                onCreatePress={() => setIsAddModalVisible(true)}
              />
              <View className="flex-row gap-3 items-stretch">
                <View className="flex-row items-center justify-center bg-ash rounded-2xl px-4 md:border md:border-ash-3">
                  <Pressable
                    onPress={() => setFilterTrigger(true)}
                    className="flex-row items-center gap-2 xl:pr-3 xl:border-r xl:border-ash-3 hover:bg-ash-3 hover:rounded-lg"
                  >
                    <Filter
                      size={18}
                      color={themeVars["--color-primary-default"] as string}
                    />
                    <Text className="text-slate text-xs hidden xl:flex">
                      {t("LABEL_FILTER")}
                    </Text>
                    {activeFilterCount > 0 && (
                      <View className="bg-primary rounded-full px-2 justify-center items-center">
                        <Text className="text-chalk text-xs">
                          {activeFilterCount}
                        </Text>
                      </View>
                    )}
                  </Pressable>
                  <View className="hidden xl:flex flex-row items-center pl-3">
                    <Text className="text-slate text-xs">{tagCountLabel}</Text>
                  </View>
                </View>

                <ToolbarCreateButton
                  text="BTN_ADD"
                  onPress={() => setIsAddModalVisible(true)}
                  hideOnMobile
                />
              </View>
            </View>

            <CategoriesTable
              searchQuery={searchQuery}
              filterTrigger={filterTrigger}
              onFilterTriggerConsumed={() => setFilterTrigger(false)}
              onFilterCountChange={setActiveFilterCount}
              onTotalCountChange={setFilteredTotalCount}
            />
          </View>

          {/* Chatbot card */}
          <View className="flex-col bg-chalk rounded-2xl p-4 gap-6 md:border md:border-ash-3">
            <View className="flex-col gap-3 md:flex-row md:justify-between md:items-center">
              <SectionHeader
                title={t("LABEL_CHATBOT")}
                subtitle={t("HINT_CHATBOT_DESCRIPTION")}
              />

              <ScButton
                text={
                  isChatbotEnabled
                    ? t("LABEL_DISABLE_CHATBOT")
                    : t("LABEL_ENABLE_CHATBOT")
                }
                mode={isChatbotEnabled ? "outlined" : "contained"}
                loading={isSettingUp || isDisabling}
                disabled={!canEnableChatbot && !isChatbotEnabled}
                onPress={
                  isChatbotEnabled ? handleDisableChatbot : handleSetupChatbot
                }
              />
            </View>

            {/* Warning message when organization config is missing */}
            {!hasOrgConfig && !isChatbotEnabled && (
              <View className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <Text className="text-amber-800 font-medium text-sm">
                  {t("HINT_ORG_CONFIG_REQUIRED") || "Organization configuration required"}
                </Text>
                <Text className="text-amber-700 text-sm mt-1">
                  {t("HINT_SET_ORG_LOGO_FIRST") ||
                    "Please set your organization logo first in the Organization Settings before enabling the chatbot."}
                </Text>
              </View>
            )}

            {agent && (
              <View className="flex-col items-center">
                <View className="bg-ash p-2 rounded-xl gap-2 items-center md:border md:border-ash-3">
                  <Image
                    source={{ uri: agent.logo_url }}
                    style={{ width: 200, height: 200, borderRadius: 8 }}
                  />
                  <Text className="font-medium text-gray-dark">
                    {agent.name.split("-").at(-1)}
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      <AddCategoryModal
        visible={isAddModalVisible}
        onAdd={handleAddCategory}
        onCancel={() => setIsAddModalVisible(false)}
        loading={isInserting}
      />
    </View>
  );
};

export default Dashboard;
